import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getBusinessById } from '@/api/endpoints/businesses';
import { Business } from '@/types/business';
import { nanoid } from 'nanoid/non-secure';
import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState, PhotoAsset } from '../types';
import { getDraftEntryPhotos, type CheckInDraft, type DraftCheckInResume } from './use-drafts';

export const MAX_ENTRIES = 5;
export const MAX_NOTE_ENTRIES = 5;

export function createEmptyNoteEntry(): NoteEntryState {
  return {
    id: `note_entry_${nanoid()}`,
    menuItem: null,
    content: '',
    photo: null,
  };
}

export function createEmptyEntry(): CheckInEntryState {
  return {
    id: `entry_${nanoid()}`,
    menuItem: null,
    ratingId: null,
    ratingKey: null,
    photos: [],
    reviewContent: '',
    recommendTo: [],
  };
}

/** A check-in that already exists for an entry, restored from a draft. */
export interface CheckInResume extends DraftCheckInResume {
  /** Local ids of the entry's photos that are already uploaded. */
  uploadedPhotoIds: string[];
}

export interface QuickCheckInState {
  business: Business | null;
  diningType: 'dine_in' | 'takeout';
  occurredAt: string | null;
  entries: CheckInEntryState[];
  /**
   * Check-ins a restored draft already created, keyed by entry id. Submitting
   * resumes against them instead of posting again.
   */
  resume?: Readonly<Record<string, CheckInResume>>;
}

/** Upload status fields written onto a local photo during submit. */
export type PhotoStatusPatch = Pick<PhotoAsset, 'isUploading' | 'uploadError'>;

/**
 * Return a new state where each photo of entry `entryId` is merged with the
 * patch `patchFor(photo)` returns. Other entries keep their identity.
 */
export function updateEntryPhotos(
  state: QuickCheckInState,
  entryId: string,
  patchFor: (photo: PhotoAsset) => PhotoStatusPatch
): QuickCheckInState {
  return {
    ...state,
    entries: state.entries.map((entry) =>
      entry.id === entryId
        ? { ...entry, photos: entry.photos.map((photo) => ({ ...photo, ...patchFor(photo) })) }
        : entry
    ),
  };
}

/** Return new note entries with the photo of `entryId` merged with `patch`. */
export function updateNoteEntryPhoto(
  entries: NoteEntryState[],
  entryId: string,
  patch: PhotoStatusPatch
): NoteEntryState[] {
  return entries.map((entry) =>
    entry.id === entryId && entry.photo ? { ...entry, photo: { ...entry.photo, ...patch } } : entry
  );
}

type DraftEntry = NonNullable<CheckInDraft['entries']>[number];

/** Restore one draft entry: photos with their alt text and MIME type, plus any resume state. */
function restoreDraftEntry(
  draftEntry: DraftEntry,
  index: number
): { entry: CheckInEntryState; resume: CheckInResume | null } {
  const id = `draft_entry_${index}`;
  const draftPhotos = getDraftEntryPhotos(draftEntry);
  const photos: PhotoAsset[] = draftPhotos.map((photo, pi) => ({
    id: `draft-photo-${index}-${pi}`,
    uri: photo.uri,
    ...(photo.altText ? { altText: photo.altText } : {}),
    ...(photo.mimeType ? { mimeType: photo.mimeType } : {}),
    isUploading: false,
  }));
  const entry: CheckInEntryState = {
    id,
    menuItem: draftEntry.menuItem
      ? ({ id: draftEntry.menuItem.id, name: draftEntry.menuItem.name } as any)
      : null,
    ratingId: draftEntry.ratingId,
    ratingKey: null,
    photos,
    reviewContent: draftEntry.reviewContent,
    recommendTo: (draftEntry as any).recommendTo ?? [],
  };
  const resume = draftEntry.resume
    ? {
        checkInId: draftEntry.resume.checkInId,
        reviewDone: draftEntry.resume.reviewDone,
        finalized: draftEntry.resume.finalized,
        uploadedPhotoIds: photos.filter((_, pi) => draftPhotos[pi].uploaded).map((p) => p.id),
      }
    : null;
  return { entry, resume };
}

export function buildInitialState(initialDraft?: CheckInDraft | null): QuickCheckInState {
  if (initialDraft) {
    const business = initialDraft.business
      ? ({ id: initialDraft.business.id, name: initialDraft.business.name } as Business)
      : null;
    const diningType = initialDraft.diningType ?? 'dine_in';

    if (initialDraft.entries && initialDraft.entries.length > 0) {
      const restored = initialDraft.entries.map(restoreDraftEntry);
      const resumeEntries = restored.flatMap(({ entry, resume }) =>
        resume ? [[entry.id, resume] as const] : []
      );
      return {
        business,
        diningType,
        occurredAt: null,
        entries: restored.map(({ entry }) => entry),
        ...(resumeEntries.length > 0 ? { resume: Object.fromEntries(resumeEntries) } : {}),
      };
    }

    return {
      business,
      diningType,
      occurredAt: null,
      entries: [
        {
          id: 'draft_entry_0',
          menuItem: initialDraft.menuItem
            ? ({ id: initialDraft.menuItem.id, name: initialDraft.menuItem.name } as any)
            : null,
          ratingId: initialDraft.ratingId,
          ratingKey: null,
          photos: initialDraft.photos.map((uri, index) => ({
            id: `draft-photo-${index}`,
            uri,
            isUploading: false,
          })),
          reviewContent: initialDraft.reviewContent,
          recommendTo: [],
        },
      ],
    };
  }

  return {
    business: null,
    diningType: 'dine_in',
    occurredAt: null,
    entries: [createEmptyEntry()],
  };
}

export interface UseCheckInFormStateOptions {
  initialDraft?: CheckInDraft | null;
  initialBusinessId?: string;
}

export function useCheckInFormState({
  initialDraft,
  initialBusinessId,
}: UseCheckInFormStateOptions = {}) {
  const [state, setState] = useState<QuickCheckInState>(() =>
    buildInitialState(initialDraft)
  );

  const [noteEntries, setNoteEntries] = useState<NoteEntryState[]>(() => [
    createEmptyNoteEntry(),
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const draftIdRef = useRef<string | null>(initialDraft?.id || null);

  const [lastSavedState, setLastSavedState] = useState<QuickCheckInState | null>(
    initialDraft ? state : null
  );

  const readyEntries = useMemo(
    () => state.entries.filter((e) => e.menuItem !== null && e.ratingId !== null),
    [state.entries]
  );

  const hasUnsavedChanges = useMemo(() => {
    const hasAnyData =
      state.business !== null ||
      state.entries.some(
        (e) =>
          e.menuItem !== null ||
          e.ratingId !== null ||
          e.photos.length > 0 ||
          e.reviewContent.trim().length > 0
      );

    if (!hasAnyData) return false;
    if (!lastSavedState) return true;

    if (state.business?.id !== lastSavedState.business?.id) return true;
    if (state.entries.length !== lastSavedState.entries.length) return true;

    return state.entries.some((entry, i) => {
      const saved = lastSavedState.entries[i];
      if (!saved) return true;
      return (
        entry.menuItem?.id !== saved.menuItem?.id ||
        entry.ratingId !== saved.ratingId ||
        entry.photos.length !== saved.photos.length ||
        entry.reviewContent !== saved.reviewContent
      );
    });
  }, [state, lastSavedState]);

  const canSubmit = useMemo(
    () => state.business !== null && readyEntries.length > 0,
    [state.business, readyEntries.length]
  );

  const readyNoteEntries = useMemo(
    () =>
      noteEntries.filter(
        (e) => e.menuItem !== null && e.content.trim().length > 0
      ),
    [noteEntries]
  );

  const canSubmitNotes = useMemo(
    () => state.business !== null && readyNoteEntries.length > 0,
    [state.business, readyNoteEntries.length]
  );

  useEffect(() => {
    if (!initialBusinessId || initialDraft || state.business) return;

    let cancelled = false;
    getBusinessById(initialBusinessId)
      .then((business) => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, business }));
        }
      })
      .catch((err) => {
        console.error('Failed to load initial business:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [initialBusinessId, initialDraft, state.business]);

  const handleSetBusiness = useCallback((business: Business) => {
    setState((prev) => ({
      ...prev,
      business,
      entries: [createEmptyEntry()],
      resume: undefined,
    }));
    setNoteEntries([createEmptyNoteEntry()]);
  }, []);

  const handleClearBusiness = useCallback(() => {
    setState((prev) => ({
      ...prev,
      business: null,
      entries: [createEmptyEntry()],
      resume: undefined,
    }));
    setNoteEntries([createEmptyNoteEntry()]);
  }, []);

  const handleSetDiningType = useCallback((diningType: 'dine_in' | 'takeout') => {
    setState((prev) => ({ ...prev, diningType }));
  }, []);

  const handleSetOccurredAt = useCallback((date: string | null) => {
    setState((prev) => ({ ...prev, occurredAt: date }));
  }, []);

  return {
    state,
    setState,
    noteEntries,
    setNoteEntries,
    isSubmitting,
    setIsSubmitting,
    isSavingDraft,
    setIsSavingDraft,
    draftIdRef,
    lastSavedState,
    setLastSavedState,
    readyEntries,
    hasUnsavedChanges,
    canSubmit,
    readyNoteEntries,
    canSubmitNotes,
    handleSetBusiness,
    handleClearBusiness,
    handleSetDiningType,
    handleSetOccurredAt,
  };
}
